import { useFlags } from 'launchdarkly-react-client-sdk';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useIsFreePlanOrganization } from '@/hooks/useIsFreePlanOrganization';

/**
 * Single source of truth for all AI chat feature flag access logic.
 *
 * Canonical access rules:
 *   hasAnyGigawattAccess = hasGigawattEntitlement || hasGigawattBasicEntitlement
 *   showTopNavAskAI                = hasGigawattEntitlement || isSetupAssistantOnlyOrg
 *                                     (full Gigawatt, not Basic — plus setup-assistant-only orgs, see TODO(TF-677) below)
 *   showSupportCenterGigawattInput = hasAnyGigawattAccess && ithGigawattSupportCenter (Basic included)
 *   isGigawattBasicOnly            = hasGigawattBasicEntitlement && !hasGigawattEntitlement (use "Gigawatt" label in nav, not "Ask AI")
 *   hasSetupAssistantOnlyAccess    = free-plan org && ithFreePlanGigawattSetupAssistant (temporary signal — see TODO(TF-677) below)
 *   isSetupAssistantOnlyOrg        = hasSetupAssistantOnlyAccess && !hasAnyGigawattAccess (excludes orgs that also carry full/basic Gigawatt)
 *   canAccessGigawattForSetupAssistant = hasAnyGigawattAccess || hasSetupAssistantOnlyAccess (gates mounting CopilotKitProvider only)
 *
 * No role checks — all users with a Gigawatt entitlement see the same thing.
 */
export const useAIChatFeatureFlags = () => {
  const {
    ithGigawattSupportCenter,
    ithEmbeddedAiChat,
    ithFreePlanGigawattSetupAssistant,
  } = useFlags();
  const hasGigawattEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Gigawatt
  );
  const hasGigawattBasicEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.GigawattBasic
  );
  const isFreePlanOrganization = useIsFreePlanOrganization();

  const hasAnyGigawattAccess =
    !!hasGigawattEntitlement || !!hasGigawattBasicEntitlement;

  // When true, nav should show "Gigawatt" label (not "Ask AI")
  const isGigawattBasicOnly =
    !!hasGigawattBasicEntitlement && !hasGigawattEntitlement;

  // Free/HR-free plan orgs have no entitlement-based Gigawatt access at all.
  // This grants them Gigawatt scoped to the setup assistant only, gated by a
  // temporary org-plan signal + LD flag until the real backend entitlement ships.
  // TODO(TF-677): replace with gigawatt_setup_assistant entitlement once core-entity ships it
  const hasSetupAssistantOnlyAccess =
    isFreePlanOrganization && !!ithFreePlanGigawattSetupAssistant;

  // hasSetupAssistantOnlyAccess alone doesn't exclude orgs that also carry
  // full/basic Gigawatt (e.g. a leftover free package on a paid org) — call
  // sites that need to scope behavior strictly to setup-assistant-only orgs
  // (tool access, completion-state fetching, upgrade nudges) should use this.
  const isSetupAssistantOnlyOrg =
    hasSetupAssistantOnlyAccess && !hasAnyGigawattAccess;

  // Top Nav "Ask AI" button: full Gigawatt (not GigawattBasic), plus
  // setup-assistant-only orgs — see TODO(TF-677) below. Uses isSetupAssistantOnlyOrg
  // (not hasSetupAssistantOnlyAccess) so a Basic org with a leftover free package
  // still gets the "Gigawatt" label instead of Ask AI.
  const showTopNavAskAI = !!hasGigawattEntitlement || isSetupAssistantOnlyOrg;

  // Support Center Gigawatt input: any Gigawatt access (including Basic), and support center flag enabled
  const showSupportCenterGigawattInput =
    hasAnyGigawattAccess && !!ithGigawattSupportCenter;

  // Gates mounting CopilotKitProvider. Top nav now includes setup-assistant-only
  // orgs (see showTopNavAskAI above), but side nav, support center input, and
  // the /ai-chat route must stay hasAnyGigawattAccess-only for these orgs.
  const canAccessGigawattForSetupAssistant =
    hasAnyGigawattAccess || hasSetupAssistantOnlyAccess;

  return {
    hasAnyGigawattAccess,
    showTopNavAskAI,
    isGigawattBasicOnly,
    showSupportCenterGigawattInput,
    hasSetupAssistantOnlyAccess,
    isSetupAssistantOnlyOrg,
    canAccessGigawattForSetupAssistant,
    ithEmbeddedAiChat: !!ithEmbeddedAiChat,
  };
};
